export const menu = {
  selector: '.v-component--menu',
  reactive: true,

  run(el, data, root) {
    const trigger = el.querySelector('[data-menu-trigger]');
    const content = el.querySelector('[data-menu-content]');
    let popperInstance = null;
    let overlay = null;
    let open = false;
    let openedWithKeyboard = false;
    let focusTimeout;

    const config = {
      openOnClick: el.dataset.openOnClick === 'true',
      openOnHover: el.dataset.openOnHover === 'true',
      closeOnOutsideClick: el.dataset.closeOnOutsideClick === 'true',
      hasOverlay: el.dataset.hasOverlay === 'true',
      placement: el.dataset.placement || 'bottom',
      openOnFocus: el.dataset.openOnFocus === 'true',
      withOverlay: el.dataset.withOverlay === 'true',
      closeOnScrollOut: el.dataset.closeOnScrollOut === 'true'
    };

    // Apply accessibility attributes
    applyAccessibility(trigger, content);

    // Get all focusable elements in the menu
    const getFocusableElements = () => {
      return content.querySelectorAll('[role="menuitem"]');
    };

    const showMenu = (keyboardTriggered = false) => {
      if (open) return;
      open = true;
      openedWithKeyboard = keyboardTriggered;
      content.style.display = 'block';
      trigger.setAttribute('aria-expanded', 'true');
      
      if (config.withOverlay) createOverlay();
      
      popperInstance = Popper.createPopper(trigger, content, { 
        placement: config.placement,
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              padding: 8,
              boundary: 'viewport'
            },
          },
        ],
      });

      // Focus first item only when opened with keyboard
      if (openedWithKeyboard) {
        setTimeout(() => {
          const focusableElements = getFocusableElements();
          if (focusableElements.length > 0) {
            focusableElements.forEach(el => el.setAttribute('tabindex', '0'));
            // focusableElements[0].focus();
          }
        }, 0);
      }
    };

    const hideMenu = () => {
      if (!open) return;
      open = false;
      content.style.display = 'none';
      trigger.setAttribute('aria-expanded', 'false');
      
      // Reset tabindex for all menu items
      const focusableElements = getFocusableElements();
      focusableElements.forEach(el => el.setAttribute('tabindex', '-1'));
      
      if (popperInstance) {
        popperInstance.destroy();
        popperInstance = null;
      }
      
      removeOverlay();
      
      // Only return focus to trigger if not using openOnFocus behavior
      if (openedWithKeyboard && !config.openOnFocus) {
        trigger.focus();
      }
      openedWithKeyboard = false;
    };

    const createOverlay = () => {
      overlay = document.createElement('div');
      overlay.className = 'menu-overlay';
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.zIndex = '999';
      document.body.appendChild(overlay);
      overlay.addEventListener('click', hideMenu);
    };

    const removeOverlay = () => {
      if (overlay) {
        overlay.removeEventListener('click', hideMenu);
        document.body.removeChild(overlay);
        overlay = null;
      }
    };

    const handleClickOutside = (e) => {
      if (!el.contains(e.target)) {
        hideMenu();
      }
    };

    const handleScroll = () => {
      if (!open || !content.getBoundingClientRect) return;
      const rect = content.getBoundingClientRect();
      if (
        rect.bottom < 0 ||
        rect.top > window.innerHeight ||
        rect.left > window.innerWidth ||
        rect.right < 0
      ) {
        hideMenu();
      }
    };

    // Keyboard navigation within menu
    const handleMenuKeydown = (e) => {
      if (!open) return;

      // For openOnFocus menus, let browser handle default Tab behavior
      if (config.openOnFocus && e.key === 'Tab') {
        return; // Allow natural tab order
      }

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          hideMenu();
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
          updateFocus(focusableElements, currentIndex, prevIndex);
          break;
          
        case 'ArrowDown':
          e.preventDefault();
          const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
          updateFocus(focusableElements, currentIndex, nextIndex);
          break;
          
        case 'Home':
          e.preventDefault();
          updateFocus(focusableElements, currentIndex, 0);
          break;
          
        case 'End':
          e.preventDefault();
          updateFocus(focusableElements, currentIndex, focusableElements.length - 1);
          break;
      }
    };

    // Helper function for focus management
    const updateFocus = (elements, oldIndex, newIndex) => {
      if (oldIndex >= 0) {
        elements[oldIndex].setAttribute('tabindex', '-1');
      }
      elements[newIndex].setAttribute('tabindex', '0');
      elements[newIndex].focus();
    };

    // Detect keyboard usage
    const handleKeydown = (e) => {
      if (['Tab', 'ArrowUp', 'ArrowDown', 'Escape'].includes(e.key)) {
        openedWithKeyboard = true;
      }
    };

    const handleMousedown = () => {
      openedWithKeyboard = false;
    };

    // Improved focus handling
    const handleTriggerFocus = () => {
      clearTimeout(focusTimeout);
      if (!open && config.openOnFocus) {
        // Check if focus came from keyboard (Tab)
        if (document.activeElement === trigger && openedWithKeyboard) {
          showMenu(true); // Pass true to indicate keyboard opening
        }
      }
    };

    const handleTriggerBlur = () => {
      focusTimeout = setTimeout(() => {
        if (!el.contains(document.activeElement)) {
          hideMenu();
        }
      }, 10);
    };

    const handleContentFocusIn = () => {
      clearTimeout(focusTimeout);
    };

    const handleContentFocusOut = (e) => {
      if (!el.contains(e.relatedTarget)) {
        hideMenu();
      }
    };

    // Event bindings
    if (config.openOnClick) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        open ? hideMenu() : showMenu(false);
      });
    }

    if (config.openOnHover) {
      trigger.addEventListener('mouseenter', () => showMenu(false));
      el.addEventListener('mouseleave', hideMenu);
    }

    if (config.openOnFocus) {
      trigger.addEventListener('focus', handleTriggerFocus);
      trigger.addEventListener('blur', handleTriggerBlur);
      content.addEventListener('focusin', handleContentFocusIn);
      content.addEventListener('focusout', handleContentFocusOut);
    }

    if (config.closeOnOutsideClick) {
      document.addEventListener('click', handleClickOutside);
    }

    if (config.closeOnScrollOut) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    // Keyboard navigation events
    if (!config.openOnFocus) {
      content.addEventListener('keydown', handleMenuKeydown);
    }
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('mousedown', handleMousedown);

    // Cleanup when el is removed
    const observer = new MutationObserver(() => {
      if (!document.body.contains(el)) {
        cleanup();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const cleanup = () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      content.removeEventListener('keydown', handleMenuKeydown);
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('mousedown', handleMousedown);
      if (overlay) removeOverlay();
      if (popperInstance) popperInstance.destroy();
      observer.disconnect();
    };
  }
};

// Accessibility Setup
function applyAccessibility(trigger, content) {
  const menuId = content.id || `menu-${Math.random().toString(36).substr(2, 9)}`;
  content.id = menuId;

  trigger.setAttribute('aria-haspopup', 'menu');
  trigger.setAttribute('aria-controls', menuId);
  trigger.setAttribute('aria-expanded', 'false');

  content.setAttribute('role', 'menu');
  content.setAttribute('tabindex', '-1');

  // Set role and tabindex for menu items
  const menuItems = content.querySelectorAll('a, button, [role="menuitem"]');
  menuItems.forEach(item => {
    item.setAttribute('role', 'menuitem');
    item.setAttribute('tabindex', '-1');
  });
}